/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { UISyncService } from '../../services/ui-sync.service';
import { filter } from 'rxjs';
import { SidebarService } from '../../services/sidebar.service';

/** A menu entry. */
export interface MenuItem {
  label: string;
  route: string;
  icon?: string;
  subItems?: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {
  currentRoute: string = '';
  activeItem: string | null = null;

  constructor(
    private router: Router,
    private uiService: UISyncService,
    public sidebar: SidebarService
  ) {
    this.currentRoute = this.router.url;
    this.syncActiveItem(this.currentRoute);
    this.updateRouteLabel(this.currentRoute);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = (event as NavigationEnd).url;
        this.syncActiveItem(this.currentRoute);
        this.updateRouteLabel(this.currentRoute);
        this.sidebar.close();
      });
  }

  menuItems: MenuItem[] = [
    { label: 'Home', icon: 'pi pi-home', route: '/dashboard' },
    {
      label: 'Orders', icon: 'pi pi-shopping-cart', route: '/orders', subItems: [
        { label: 'All Orders', route: '/orders/order-list' },
        { label: 'Cancelation requests', route: '/orders/cancellations' },
        { label: 'Returned', route: '/orders/return-order-list' },
        // { label: 'GRV Management', route: '/finance/grv-management' },
        { label: 'Extended Warranty Management', route: '/orders/extended-warranty' },
      ]
    },
    { label: 'Media', icon: 'pi pi-images', route: '/media' },
    {
      label: 'Products', icon: 'pi pi-box', route: '/products', subItems: [
        { label: 'Catalogue', route: '/products/catalogue' },
        { label: 'Add Product', route: '/products/add' },
        { label: 'Categories', route: '/products/categories' },
        { label: 'Brands', route: '/products/brands' },
        { label: 'Tags', route: '/products/tags' },
        { label: 'Primary colour', route: '/products/primary-colour' },
        { label: 'Attributes', route: '/products/attributes' },
        { label: 'Inventory Intelligence', route: '/products/inventory-performance' },
      ]
    },
    { label: 'Reviews', icon: 'pi pi-star', route: '/reviews' },
    { label: 'Wishlist', icon: 'pi pi-heart', route: '/wishlist' },
    {
      label: 'Accounts', icon: 'pi pi-users', route: '/accounts/staff', subItems: [
        { label: 'Users', route: '/accounts/customer' },
        { label: 'Staff', route: '/accounts/staff' },
      ]
    },
    {
      label: 'Finance', icon: 'pi pi-money-bill', route: '/finance', subItems: [
        { label: 'Order Finance', route: '/finance/overview' },
        { label: 'Sale Reports', route: '/finance/sales-reports' },
        { label: 'Profit Reports', route: '/finance/profit-reports' },
        { label: 'Stock Reports', route: '/finance/stock-reports' },
        { label: 'Supplier Management', route: '/finance/supplier-management' },
        { label: 'GRV Management', route: '/finance/grv-management' },
        { label: 'Return Report', route: '/finance/reports/return' },
        { label: 'Accountant Orders Report', route: '/finance/accountant-orders-report' },
        { label: 'Quotation Requests', route: '/finance/quotation-requests' },
      ]
    },
    {
      label: 'Marketing', icon: 'pi pi-megaphone', route: '/marketing', subItems: [
        // { label: 'Campaigns', route: '/marketing/campaigns' },
        // { label: 'Promotions', route: '/marketing/promotions' },
        { label: 'Coupons', route: '/marketing/coupons' },
        { label: 'User Searches', route: '/marketing/user-searches' },
        // Notification Center is temporarily hidden while backend processing is paused.
        // { label: 'Notification center', route: '/marketing/notification-center' },
        { label: 'Deals', route: '/marketing/deals' },
        { label: 'Influencers Management', route: '/marketing/influencers-management' },
        { label: 'SEO', route: '/seo' },
        { label: 'Search Keyword Manager', route: '/marketing/seo' },
        { label: 'Synonyms Text Manager', route: '/marketing/synonyms-text-manager' },
        { label: 'Marketing Performance', route: '/marketing/marketing-performance' },
        { label: 'FAQ Management', route: '/marketing/faq-management' },
        { label: 'Blogs Management', route: '/marketing/blog-management' }
      ]
    },
    {
      label: 'Analytics', icon: 'pi pi-chart-bar', route: '/analytics', subItems: [
        { label: 'Website Analytics', route: '/analytics/website' },
        { label: 'Application Analytics', route: '/analytics/application' },
        { label: 'Traffic & Source Reports', route: '/analytics/traffic' },
        // Analytics v2 pages hidden until production data/results are fully validated.
        // { label: 'Conversion and traffic', route: '/analytics/conversion' },
        // { label: 'Revenue & Category Insights', route: '/analytics/marketing' },
        // { label: 'Sales', route: '/analytics/sales' },
        // { label: 'Customers', route: '/analytics/customers' },
        // // { label: 'Products', route: '/analytics/products' },
        // { label: 'Revenue & Category Insights', route: '/analytics/marketing' },
        // { label: 'Conversion and traffic', route: '/analytics/conversion' },
        // { label: 'Product Analytics', route: '/analytics/product-analytics' },
        // { label: 'Customer Segmentation', route: '/analytics/customer-segmentation' },
        // { label: 'Sales Timing Insights', route: '/analytics/sales-timing-insights' },
        // { label: 'Returns & Promo Codes', route: '/analytics/returns-promo' },
        // { label: 'Order Analytics', route: '/analytics/order-analytics' },
        // { label: 'Analytics Webpage', route: '/analytics/analytics-webpage' },
        // { label: 'Application Analytics', route: '/analytics/application-analytics' },
      ]
    },
    {
      label: 'Shipping', icon: 'pi pi-truck', route: '/shipping', subItems: [
        { label: 'Method List', route: '/shipping/method-list' },
        { label: 'Company Management', route: '/shipping/shipping-companies-management' },
      ]
    },
    {
      label: 'Home Page ',
      icon: 'pi pi-cog',
      route: '/custom',
      subItems: [
        { label: 'Home Pages Meta', route: '/custom/home-page-meta-setting' },
        { label: 'Home Page Sections', route: '/custom/home-page-sections' },
        { label: 'Tab Bar Settings', route: '/custom/tab-bar-settings' },
        { label: 'Media Banner Setup', route: '/custom/media-banner-setup' }
      ]
    },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      route: '/settings',
      subItems: [
        { label: 'Application Splash Screen', route: '/settings/application-splash-screen' },
        { label: 'Media Page Settings', route: '/settings/media-page-settings' },
        { label: 'Used Tech section settings', route: '/settings/used-section-settings' },
        { label: 'Bazaar section settings', route: '/settings/bazaar-section-settings' },
        { label: 'Brands page settings', route: '/settings/brands-page-settings' },
        { label: 'Our Branches', route: '/settings/branches' },
        { label: 'Payment Methods', route: '/settings/payment-methods' },
        { label: 'Cookie Consent Banner', route: '/settings/cookie-consent-banner' },
        { label: 'General SEO Settings', route: '/settings/general-seo-settings' }
      ]
    },
    // {
    //   label: 'System Tools', icon: 'pi pi-wrench', route: '/system-tools', subItems: [
    //     { label: 'Search Index & Cache', route: '/system-tools/search-index' },
    //   ]
    // },
  ];

  toggleSubMenu(item: string): void {
    this.activeItem = this.activeItem === item ? null : item;
  }

  isActiveRoute(route: string): boolean {
    return this.currentRoute.startsWith(route);
  }

  // Helper method for navigating to add/edit product
  navigateToProduct(productId?: string): void {
    if (productId) {
      this.router.navigate(['/products/add', productId]);
    } else {
      this.router.navigate(['/products/add']);
    }
  }

  private syncActiveItem(url: string): void {
    const activeParent = this.menuItems.find((menuItem) =>
      !!menuItem.subItems?.some((subItem) => url.startsWith(subItem.route))
    );

    this.activeItem = activeParent?.label ?? null;
  }

  // Update route label based on current URL
  private updateRouteLabel(url: string): void {
    // F9 — the heading read "Inventory Performance" while both the spec
    // ("Inventory Intelligence Documentation V2") and the sidebar entry for this
    // very route read "Inventory Intelligence", so the page named itself one
    // thing and the nav that reached it another. The special case survives only
    // because the route sits under /products and would otherwise resolve to the
    // parent's label; the TEXT is what changed — route, files and API paths are
    // untouched.
    if (url.startsWith('/products/inventory-performance')) {
      this.uiService.selectedRouteLabel = 'Inventory Intelligence';
      return;
    }

    let label = 'Dashboard';
    for (const menuItem of this.menuItems) {
      if (url.startsWith(menuItem.route)) {
        if (menuItem.subItems) {
          const sub = menuItem.subItems.find(s => url.startsWith(s.route));
          label = sub ? sub.label : menuItem.label;
        } else {
          label = menuItem.label;
        }
        break;
      }
    }
    this.uiService.selectedRouteLabel = label;
  }
}
